if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.activeMediaId = null;
        this.bindLightboxDismissGuard();
        this.buildLightboxUI();
        this.addEventListener('keydown', this.onKeyDown.bind(this));
      }

      bindLightboxDismissGuard() {
        this.addEventListener(
          'pointerup',
          (event) => {
            if (
              event.target.closest(
                '.ssw-lightbox__nav, .product-media-modal__content, .product-media-modal__toggle, .ssw-lightbox__counter'
              )
            ) {
              event.stopImmediatePropagation();
            }
          },
          true
        );
      }

      buildLightboxUI() {
        this.classList.add('ssw-lightbox');
        const dialog = this.querySelector('.product-media-modal__dialog');
        if (!dialog || dialog.querySelector('.ssw-lightbox__prev')) return;

        const chevron = `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="20" height="20"><path d="M14.5 5.5L8 12l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

        this.prevBtn = document.createElement('button');
        this.prevBtn.type = 'button';
        this.prevBtn.className = 'ssw-lightbox__nav ssw-lightbox__prev';
        this.prevBtn.setAttribute('aria-label', 'Previous image');
        this.prevBtn.innerHTML = chevron;

        this.nextBtn = document.createElement('button');
        this.nextBtn.type = 'button';
        this.nextBtn.className = 'ssw-lightbox__nav ssw-lightbox__next';
        this.nextBtn.setAttribute('aria-label', 'Next image');
        this.nextBtn.innerHTML = chevron;

        this.counter = document.createElement('div');
        this.counter.className = 'ssw-lightbox__counter';
        this.counter.setAttribute('aria-live', 'polite');

        dialog.append(this.prevBtn, this.nextBtn, this.counter);

        const stopDismiss = (event) => {
          event.stopPropagation();
          event.stopImmediatePropagation();
        };

        this.prevBtn.addEventListener('pointerup', stopDismiss);
        this.nextBtn.addEventListener('pointerup', stopDismiss);
        this.prevBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(-1);
        });
        this.nextBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(1);
        });

        this.bindSwipeNavigation();
      }

      bindSwipeNavigation() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        let touchStartX = 0;
        let touchStartY = 0;

        content.addEventListener(
          'touchstart',
          (event) => {
            touchStartX = event.changedTouches[0].screenX;
            touchStartY = event.changedTouches[0].screenY;
          },
          { passive: true }
        );

        content.addEventListener(
          'touchend',
          (event) => {
            const touchEndX = event.changedTouches[0].screenX;
            const touchEndY = event.changedTouches[0].screenY;
            const deltaX = touchEndX - touchStartX;
            const deltaY = touchEndY - touchStartY;

            if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;

            this.navigate(deltaX > 0 ? -1 : 1);
          },
          { passive: true }
        );
      }

      getMediaItems() {
        const container = this.querySelector('.product-media-modal__content');
        if (!container) return [];

        return [...container.children].filter((child) => child.hasAttribute('data-media-id'));
      }

      findDeferredMedia(element) {
        if (!element) return null;

        if (element.matches('deferred-media, product-model')) {
          return element;
        }

        return element.querySelector('deferred-media, product-model');
      }

      loadActiveMedia(element) {
        const deferredMedia = this.findDeferredMedia(element);
        if (!deferredMedia || typeof deferredMedia.loadContent !== 'function') return;

        deferredMedia.loadContent(false);

        const video = deferredMedia.querySelector('video');
        if (!video) return;

        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.controls = true;

        const playPromise = video.play();
        if (playPromise && playPromise.catch) {
          playPromise.catch(() => {});
        }
      }

      navigate(direction) {
        const items = this.getMediaItems();
        if (items.length < 2) return;

        window.pauseAllMedia();

        const currentIndex = items.findIndex(
          (item) => item.getAttribute('data-media-id') === this.activeMediaId
        );
        let nextIndex = currentIndex + direction;

        if (nextIndex < 0) nextIndex = items.length - 1;
        if (nextIndex >= items.length) nextIndex = 0;

        this.activeMediaId = items[nextIndex].getAttribute('data-media-id');
        this.showActiveMedia();
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.navigate(-1);
        }

        if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.navigate(1);
        }
      }

      hide() {
        super.hide();
        this.activeMediaId = null;
      }

      show(opener) {
        super.show(opener);
        this.activeMediaId = opener.getAttribute('data-media-id');
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.activeMediaId || this.openedBy?.getAttribute('data-media-id');
        if (!mediaId) return;

        window.pauseAllMedia();

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === mediaId);
        });

        const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.loadActiveMedia(activeMedia);
        this.updateLightboxUI();
      }

      updateLightboxUI() {
        const items = this.getMediaItems();
        const index = items.findIndex((item) => item.getAttribute('data-media-id') === this.activeMediaId);
        const hasMultiple = items.length > 1;

        if (this.counter) {
          this.counter.textContent = hasMultiple ? `${index + 1} / ${items.length}` : '';
          this.counter.hidden = !hasMultiple;
        }

        if (this.prevBtn) this.prevBtn.hidden = !hasMultiple;
        if (this.nextBtn) this.nextBtn.hidden = !hasMultiple;
      }
    }
  );
}
